import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import withProgress from 'core/components/progress/withProgress'
import Dropdown from 'core/elements/dropdown'
import useListAction from 'core/hooks/useListAction'
import { getPortsForVm } from 'openstack/components/networks/actions'
import { listFloatingIps } from 'openstack/components/networks/floating-ips/actions'
import { floatingIpsSelector } from 'openstack/components/networks/floating-ips/selectors'

const AsyncDropdown = withProgress(Dropdown, { inline: true })

export default function AvailableVmPortsPicklist({
  disabled = false,
  name = 'vmPort',
  label = 'VM Port',
  vmId,
  ...rest
}) {
  const [vmPorts, setVmPorts] = useState([])
  const [loadingVmPorts, setLoadingVmPorts] = useState(true)

  // Load floating IPs and VMs to determine which ports are available
  const { loading: loadingFloatingIps } = useListAction(listFloatingIps)
  const floatingIps = useSelector(floatingIpsSelector)

  useEffect(() => {
    // If no VM is selected, we don't need to fetch ports
    if (!vmId) {
      setVmPorts([])
      setLoadingVmPorts(false)
      return
    }

    // Fetch ports for the selected VM
    const getPorts = async () => {
      setVmPorts([])
      setLoadingVmPorts(true)
      try {
        const response = await getPortsForVm({ vmId })
        setVmPorts(response)
      } catch (e) {
        console.log(e)
      } finally {
        setLoadingVmPorts(false)
      }
    }

    getPorts()
  }, [vmId])

  const options = useMemo(() => {
    // Get port IDs that have floating IPs assigned, filter out nulls
    const portsWithFloatingIp = floatingIps.map((floatingIp) => floatingIp?.port_id).filter(Boolean)

    // Get free ports for the selected VM. The currently selected port stays listed even once
    // it has a floating IP (e.g. right after it's been assigned, while the modal is still
    // open), so the dropdown doesn't go blank
    const freeVmPorts = vmPorts.filter((port) => {
      return port.id === rest.value || !portsWithFloatingIp.includes(port.id)
    })

    return freeVmPorts.map((port) => ({
      label: `${port?.fixed_ips?.[0]?.ip_address} (${port?.name || port?.id})`,
      value: port.id,
    }))
  }, [vmPorts, vmId, floatingIps, rest.value])

  return (
    <AsyncDropdown
      {...rest}
      name={name}
      label={label}
      compact={false}
      disabled={disabled}
      loading={loadingVmPorts || loadingFloatingIps}
      enableSearch
      items={options}
    />
  )
}
